import React, { useState } from 'react'
import { Table, Upload } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import { Column, Row } from '@/components/layout/layout'
import $msg from '@/utils/message'
import { useUploadXlsx } from '@/views/main/modules/goods-manage/components/import-good/table-import/useUploadXlsx'
import { importMemberSheet } from '../api'
import { memberManageModel } from '../model'
import { ImportTips } from './import-layout'
import {
    getCanonicalSheetRows,
    getMissingColumns,
    getPreviewRows,
    MemberSheetRow,
    partitionMemberSheetRows,
    requiredColumns,
    requiredInputColumns,
    sheetColumns,
} from './table-import-utils'

interface Props {
    onImportStarted?: () => void
    importing?: boolean
}

const TableImport: React.FC<Props> = props => {
    const importTemplateUrl =
        'https://d.senguo.cc/ls/tpl/%E4%BC%9A%E5%91%98%E6%95%B0%E6%8D%AE%E8%A1%A8%E6%A0%BC-%E7%A4%BA%E4%BE%8B-2020-09-28.xlsx'
    const importTips = [
        <>
            表格需包含列：{requiredInputColumns.join('、')}；生日、会员密码、积分、会员卡号为选填列，生日格式为
            YYYYMMDD，例如 19900101。
            <a href={importTemplateUrl} target="_blank" rel="noopener noreferrer">
                下载会员导入示例表格
            </a>
        </>,
        '导入开始后可离开此页面，系统会保持后台导入进程；重复会员需在导入状态提示中继续处理。',
    ]
    const [uploading, setUploading] = useState(false)
    const [sheetUrl, setSheetUrl] = useState('')
    const [importStarted, setImportStarted] = useState(false)
    const [previewList, setPreviewList] = useState<MemberSheetRow[]>([])
    const [invalidRows, setInvalidRows] = useState<Array<{ row: MemberSheetRow; reason: string }>>([])
    const { upload: uploadXlsx } = useUploadXlsx({
        complete: url => {
            setSheetUrl(url)
            setUploading(false)
            $msg('success', '表格上传成功')
        },
        error: () => {
            setUploading(false)
        },
    })

    const importReq = useRequest(importMemberSheet, {
        manual: true,
        onSuccess: () => {
            memberManageModel.setImportingMemberData(true)
            setImportStarted(true)
            $msg('success', '已开始导入')
            props.onImportStarted && props.onImportStarted()
        },
        onError: e => $msg('error', e.message || '导入表格失败'),
    })
    const columns: ColumnsType<MemberSheetRow> = sheetColumns.map(column => ({
        title: column.title,
        dataIndex: column.dataIndex,
        width: 130,
        render: value => value || '-',
    }))

    const onSubmit = async () => {
        if (props.importing) {
            $msg('warning', '当前有导入任务正在进行，请先等待完成')

            return
        }
        if (!sheetUrl) {
            $msg('warning', '请先上传会员表格')

            return
        }
        importReq.run({ sheet_url: sheetUrl })
    }

    const validateAndUpload = (file: File) => {
        if (!/\.xlsx?$/i.test(file.name)) {
            $msg('warning', '请上传 xls 或 xlsx 表格')

            return false
        }

        const reader = new FileReader()
        reader.onload = e => {
            import('xlsx')
                .then(XLSX => {
                    const workbook = XLSX.read(e.target?.result, { type: 'binary' })
                    const sheet = workbook.Sheets[workbook.SheetNames[0]]
                    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as string[][]
                    const header = rows[0] || []
                    const missingColumns = getMissingColumns(header)
                    if (missingColumns.length) {
                        $msg('warning', `表格缺少列：${missingColumns.join('、')}`)

                        return
                    }
                    const previewRows = getPreviewRows(rows)

                    if (!previewRows.length) {
                        $msg('warning', '表格没有可导入的会员数据')

                        return
                    }
                    const partition = partitionMemberSheetRows(previewRows)
                    setInvalidRows(partition.invalidRows)
                    if (!partition.validRows.length) {
                        $msg('warning', '表格没有有效会员数据，请下载失败数据检查')
                        setPreviewList([])
                        setSheetUrl('')

                        return
                    }

                    if (partition.invalidRows.length) {
                        $msg('warning', `已跳过 ${partition.invalidRows.length} 条无效数据，请下载失败数据核对`)
                    }

                    setUploading(true)
                    setSheetUrl('')
                    setImportStarted(false)
                    setPreviewList(partition.validRows)
                    const normalizedSheet = XLSX.utils.aoa_to_sheet(getCanonicalSheetRows(partition.validRows))
                    const normalizedWorkbook = XLSX.utils.book_new()
                    XLSX.utils.book_append_sheet(
                        normalizedWorkbook,
                        normalizedSheet,
                        workbook.SheetNames[0] || '会员导入',
                    )
                    const normalizedFile = new File(
                        [XLSX.write(normalizedWorkbook, { bookType: 'xlsx', type: 'array' })],
                        file.name.replace(/\.xls$/i, '.xlsx'),
                        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                    )
                    uploadXlsx(normalizedFile)
                })
                .catch(() => $msg('error', '表格解析失败，请检查文件格式'))
        }
        reader.onerror = () => $msg('error', '读取表格失败，请重新选择文件')
        reader.readAsBinaryString(file)

        return false
    }

    const downloadInvalidRows = async () => {
        const XLSX = await import('xlsx')
        const rows = [
            [...requiredColumns, '失败原因'],
            ...invalidRows.map(({ row, reason }) => [
                ...sheetColumns.map(column => row[column.dataIndex] ?? ''),
                reason,
            ]),
        ]
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), '失败数据')
        XLSX.writeFile(workbook, '会员导入失败数据.xlsx')
    }

    return (
        <Column className="member-import__panel" data-align-items-center>
            <ImportTips tips={importTips} className="member-import__tips" />
            {invalidRows.length ? (
                <div className="member-import__status">
                    已跳过 {invalidRows.length} 条无效数据。
                    <Button types="text" onClick={downloadInvalidRows}>
                        下载失败数据
                    </Button>
                </div>
            ) : null}
            <div className="member-import__table">
                <Table
                    bordered
                    pagination={false}
                    rowKey="id"
                    columns={columns}
                    loading={uploading}
                    dataSource={previewList}
                    scroll={{ y: 226, x: 'max-content' }}
                />
                {!previewList.length ? (
                    <div className="member-import__table-upload">
                        <Upload
                            accept=".xls,.xlsx,application/vnd.ms-excel"
                            showUploadList={false}
                            beforeUpload={validateAndUpload}
                        >
                            <Button types="default" loading={uploading}>
                                上传会员表格
                            </Button>
                        </Upload>
                    </div>
                ) : null}
                <Row className="member-import__table-bottom" data-align-items-center>
                    {previewList.length ? (
                        <>
                            <Row data-align-items-center data-font-size-14>
                                共
                                <span data-color-green data-margin-h-4>
                                    {previewList.length}
                                </span>
                                条数据
                            </Row>
                            <Row data-align-items-center>
                                {importStarted ? (
                                    <div className="member-import__table-des">
                                        导入过程中，您可以放心离开此页面
                                        <br />
                                        将为您保持后台导入进程
                                    </div>
                                ) : (
                                    <Upload
                                        accept=".xls,.xlsx,application/vnd.ms-excel"
                                        showUploadList={false}
                                        beforeUpload={validateAndUpload}
                                    >
                                        <Button types="primary">重新上传</Button>
                                    </Upload>
                                )}
                                <Button
                                    types="default"
                                    loading={importReq.loading}
                                    disabled={!sheetUrl || uploading || importStarted || props.importing}
                                    onClick={onSubmit}
                                    className="member-import__start-button"
                                >
                                    {importStarted ? '导入中' : '开始导入'}
                                </Button>
                            </Row>
                        </>
                    ) : null}
                </Row>
            </div>
        </Column>
    )
}

export default TableImport
